/**
 * Desktop Mega Menu custom element.
 *
 * Renders L1 nav links in the header and a fixed-position, slide-down dropdown
 * panel on mouseover. Keyboard accessible (Escape to close).
 *
 * Only initialises above the 964 px breakpoint; the mobile slideout drawer
 * continues to handle navigation below that threshold.
 */
class DesktopMegaMenu extends HTMLElement {
  static BREAKPOINT = 964;
  static CLOSE_DELAY = 150; // ms grace period before closing (prevents accidental close)

  connectedCallback() {
    this.openPanel = null;
    this.closeTimeout = null;

    if (!this.isDesktop()) return;
    this.init();

    this._onResize = this.onResize.bind(this);
    window.addEventListener('resize', this._onResize, { passive: true });

    // Keep panel position in sync when the page is scrolled with a sticky header
    this._onScroll = this.updateHeaderBottomPosition.bind(this);
    window.addEventListener('scroll', this._onScroll, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('scroll', this._onScroll);
    document.removeEventListener('click', this._onDocClick);
  }

  isDesktop() {
    return window.matchMedia(`(min-width: ${DesktopMegaMenu.BREAKPOINT}px)`).matches;
  }

  init() {
    this.scrim = document.getElementById('mega-menu-scrim');
    this.updateHeaderBottomPosition();
    this.buildDesktopNav(); // must run before hover/keyboard setup
    this.setupHoverInteractions();
    this.setupKeyboardNavigation();
    this.setupOutsideClick();
  }

  // ── Build desktop nav ──────────────────────────────────────────────────────
  //
  // Desktop panels are server-side rendered by mega-menu-item.liquid directly
  // from block.settings.menu.links (correct content, no "Shop All" rows, no
  // fragile DOM-selector reading).  Each panel sits in the mobile <li> with
  // `hidden` set to prevent FOUC.  We teleport it into the desktop nav here.

  buildDesktopNav() {
    this.chevron_down = document.querySelector('.menu-chevrons .menu-chevron-down svg').outerHTML;
    this.chevron_up = document.querySelector('.menu-chevrons .menu-chevron-up svg').outerHTML;
    const nav = this.querySelector('.desktop-mega-menu__nav');
    if (!nav) return;
    nav.classList.add('fixed-utility-m-em');
    const mobileSlideout = document.getElementById('mobile-nav-slideout');
    if (!mobileSlideout) return;

    const mobileItems = mobileSlideout.querySelectorAll(
      '.slideout-menu-drawer__item[data-desktop-url][data-desktop-title]',
    );
    if (!mobileItems.length) return;

    mobileItems.forEach((mobileItem) => {
      const url = mobileItem.dataset.desktopUrl;
      const title = mobileItem.dataset.desktopTitle;

      if (!url || !title) return;

      const li = document.createElement('li');
      li.className = 'desktop-mega-menu__item';

      // Panel pre-built by Liquid, currently sitting inside the mobile <li>
      const preBuiltPanel = mobileItem.querySelector('.desktop-mega-menu__panel');
      const hasSubmenu = mobileItem.querySelector('.navigation-drawer__right-icon') !== null;

      const a = document.createElement('a');
      a.href = url;
      a.className = 'desktop-mega-menu__l1-link';
      a.textContent = title;
      li.appendChild(a);
      if (hasSubmenu) {
        const button = document.createElement('button');
        button.className = 'desktop-mega-menu__l1-button';
        button.innerHTML = this.chevron_down;
        button.setAttribute('role', 'menuitem');
        button.setAttribute('tabindex', '0');

        if (preBuiltPanel) {
          button.setAttribute('aria-expanded', 'false');
          button.setAttribute('aria-controls', preBuiltPanel.id);
        }
        button.ariaLabel = title;
        li.appendChild(button);
      } else {
        a.className += ' no-submenu';
      }

      if (preBuiltPanel) {
        // Teleport: removes from mobile DOM, appends to desktop nav item
        preBuiltPanel.removeAttribute('hidden');
        li.appendChild(preBuiltPanel);
      }

      nav.appendChild(li);
    });

    // force update tooltips if there were any in megamenu
    document.querySelectorAll('.desktop-mega-menu__column tooltip-component').forEach((tooltip) => {
      //eslint-disable-next-line no-self-assign
      tooltip.outerHTML = tooltip.outerHTML;
    });
  }

  // ── Header bottom position ──────────────────────────────────────────────────
  updateHeaderBottomPosition() {
    const headerWrapper = document.querySelector('.header-wrapper');
    if (!headerWrapper) return;
    const bottom = Math.round(headerWrapper.getBoundingClientRect().bottom);
    document.documentElement.style.setProperty('--header-bottom-position-desktop', `${bottom}px`);
  }

  // ── Hover interactions ──────────────────────────────────────────────────────
  setupHoverInteractions() {
    this.querySelectorAll('.desktop-mega-menu__item').forEach((item) => {
      item.addEventListener('mouseenter', () => {
        clearTimeout(this.closeTimeout);
        const link = item.querySelector('.desktop-mega-menu__l1-link');
        const panel = item.querySelector('.desktop-mega-menu__panel');
        this.showPanel(item, link, panel);
      });

      item.addEventListener('mouseleave', () => {
        this.closeTimeout = setTimeout(() => this.closeAll(), DesktopMegaMenu.CLOSE_DELAY);
      });
    });

    // Re-entering the columns area cancels the pending close; leaving it starts the timer
    this.querySelectorAll('.desktop-mega-menu__columns').forEach((columns) => {
      columns.addEventListener('mouseenter', () => clearTimeout(this.closeTimeout));
      columns.addEventListener('mouseleave', (e) => {
        // The panel's slide-in animation (transform: translateY(-0.5rem) → 0) can move
        // the columns downward past a stationary cursor that is still hovering a nav
        // link above.  In that case relatedTarget lands on the nav item — don't start
        // the close timer, the cursor never actually left the mega-menu area.
        if (this.contains(e.relatedTarget)) return;
        this.closeTimeout = setTimeout(() => this.closeAll(), DesktopMegaMenu.CLOSE_DELAY);
      });
    });
  }

  showPanel(item, link, panel) {
    // Close previously-open panel instantly so there is no overlap
    if (this.openPanel && this.openPanel !== panel) {
      this.closeAll();
    }

    if (!panel) {
      // L1 link with no submenu — just close any open panel
      this.closeAll();
      return;
    }

    this.updateHeaderBottomPosition();

    item.classList.add('is-open');
    panel.classList.add('is-open');
    if (link?.nodeName !== 'A') {
      link?.setAttribute('aria-expanded', 'true');
      link ? (link.innerHTML = this.chevron_up) : null;
    } else {
      const button = item.querySelector('.desktop-mega-menu__l1-button');
      button ? (button.innerHTML = this.chevron_up) : null;
    }
    this.scrim?.classList.add('is-open');
    this.openPanel = panel;
  }

  closeAll() {
    clearTimeout(this.closeTimeout);

    this.querySelectorAll('.desktop-mega-menu__item.is-open').forEach((item) => {
      item.classList.remove('is-open');
      const button = item.querySelector('.desktop-mega-menu__l1-button');
      const panel = item.querySelector('.desktop-mega-menu__panel');
      button?.setAttribute('aria-expanded', 'false');
      button ? (button.innerHTML = this.chevron_down) : null;
      panel?.classList.remove('is-open');
    });

    this.scrim?.classList.remove('is-open');
    this.openPanel = null;
  }

  // ── Keyboard navigation ─────────────────────────────────────────────────────

  setupKeyboardNavigation() {
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        const activeItem = this.querySelector('.desktop-mega-menu__item.is-open');
        const trigger = activeItem?.querySelector('.desktop-mega-menu__l1-button');
        this.closeAll();
        trigger?.focus();
      }

      // Arrow right / left: move between L1 links
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        const l1Links = [...this.querySelectorAll('.desktop-mega-menu__l1-link')];
        const current = document.activeElement;
        const idx = l1Links.indexOf(current);
        if (idx === -1) return;
        event.preventDefault();
        const next = event.key === 'ArrowRight' ? l1Links[idx + 1] : l1Links[idx - 1];
        next?.focus();
      }
    });

    // Open panel on enter/space (keyboard users tabbing to L1 links)
    this.querySelectorAll('.desktop-mega-menu__l1-button').forEach((link) => {
      link.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          const item = link.closest('.desktop-mega-menu__item');
          const panel = item?.querySelector('.desktop-mega-menu__panel');
          if (panel) this.showPanel(item, link, panel);
        }
      });
    });

    // Close when focus leaves the entire mega menu
    this.addEventListener('focusout', () => {
      // Use a timeout so we check after the browser has moved focus
      setTimeout(() => {
        if (!this.contains(document.activeElement)) {
          this.closeAll();
        }
      }, 0);
    });
  }

  // ── Outside click ───────────────────────────────────────────────────────────

  setupOutsideClick() {
    this._onDocClick = (event) => {
      if (!this.contains(event.target)) {
        this.closeAll();
      }
    };
    document.addEventListener('click', this._onDocClick);
  }

  // ── Resize handling ─────────────────────────────────────────────────────────

  onResize() {
    if (this.isDesktop()) {
      this.updateHeaderBottomPosition();
    } else {
      this.closeAll();
    }
  }
}

customElements.define('desktop-mega-menu', DesktopMegaMenu);
